import type { Section } from "./types";

/**
 * Merges a user's customized dashboard layout with an FI specified map
 * of sections and indexes.
 */
export const applyPositionOverrides = <T extends string>(
  list: T[],
  overrides: Partial<Record<T, number>>,
): T[] => {
  const result = [...list];
  (Object.entries(overrides) as [string, number][]).forEach(
    ([name, newIndex]) => {
      const currentIndex = result.indexOf(name as T);
      if (currentIndex > -1) {
        result.splice(currentIndex, 1);
      }
      result.splice(newIndex, 0, name as T);
    },
  );
  return result;
};

/**
 * Updates a user's customized section order (mobile, and left or right web columns)
 * with all sections that should or should not be shown.
 */
export const enforceVisibleSections = <T extends Section>(
  userSectionOrder: T[],
  _allVisibleSections: T[],
): T[] => {
  const visibleSections: T[] = [];
  const remainingSections = [..._allVisibleSections];

  userSectionOrder.forEach((userSection) => {
    if (remainingSections.includes(userSection)) {
      visibleSections.push(userSection);
      remainingSections.splice(remainingSections.indexOf(userSection), 1);
    }
  });

  visibleSections.push(...remainingSections);

  return [...new Set(visibleSections)];
};
